import axios from 'axios';
import {
  Franchise,
  Product,
  Order,
  Inventory,
  Customer,
  Employee,
  Transaction,
  Vendor,
  Analytics,
  parseApiResponse,
  serializeModel
} from '../models';

// API Configuration
const API_BASE_URL = 'https://api.bhartigoldbusiness.com/api';
const FRANCHISE_API_URL = `${API_BASE_URL}/franchise`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Global error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Optional: redirect only if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ====================== SERVICES ======================

export const authService = {
  async login(username, password) {
    try {
      const response = await apiClient.post(`${FRANCHISE_API_URL}/login`, {
        user_id: username,
        password
      });

      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }

      return {
        success: true,
        data: response.data,
        user: parseApiResponse(Franchise, response.data.franchise)
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  },

  async register(franchiseData) {
    try {
      const response = await apiClient.post(
        `${FRANCHISE_API_URL}/auth/register`,
        serializeModel(franchiseData)
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  },

  async logout() {
    try {
      await apiClient.post(`${FRANCHISE_API_URL}/auth/logout`);
    } finally {
      // Always clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
    return { success: true };
  },

  async refreshToken() {
    try {
      const response = await apiClient.post(`${FRANCHISE_API_URL}/auth/refresh`);
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }
      return { success: true, token: response.data.token };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }
};

// Franchise Service
export const franchiseService = {
  async getProfile() {
    const response = await apiClient.get(`${FRANCHISE_API_URL}/profile`);
    return {
      success: true,
      data: parseApiResponse(Franchise, response.data)
    };
  },

  async updateProfile(profileData) {
    const response = await apiClient.put(
      `${FRANCHISE_API_URL}/profile`,
      serializeModel(profileData)
    );
    return {
      success: true,
      data: parseApiResponse(Franchise, response.data)
    };
  },

  async getDashboardStats() {
    const response = await apiClient.get(`${FRANCHISE_API_URL}/stats`);
    return { success: true, data: response.data };
  }
};

// Product Service
export const productService = {
  async getProducts(params = {}) {
    const response = await apiClient.get(`${FRANCHISE_API_URL}/products`, { params });
    return {
      success: true,
      data: parseApiResponse(Product, response.data.products),
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit
    };
  },

  async getProduct(id) {
    const response = await apiClient.get(`${FRANCHISE_API_URL}/products/${id}`);
    return {
      success: true,
      data: parseApiResponse(Product, response.data)
    };
  },

  async createProduct(productData) {
    const response = await apiClient.post(
      `${FRANCHISE_API_URL}/products`,
      serializeModel(productData)
    );
    return {
      success: true,
      data: parseApiResponse(Product, response.data)
    };
  },

  async updateProduct(id, productData) {
    const response = await apiClient.put(
      `${FRANCHISE_API_URL}/products/${id}`,
      serializeModel(productData)
    );
    return {
      success: true,
      data: parseApiResponse(Product, response.data)
    };
  },

  async deleteProduct(id) {
    await apiClient.delete(`${FRANCHISE_API_URL}/products/${id}`);
    return { success: true };
  },

  async getCategories() {
    const response = await apiClient.get(`${FRANCHISE_API_URL}/products/categories`);
    return { success: true, data: response.data };
  }
};

// ... (other services remain the same with improved error handling)

export const orderService = { /* ... same pattern as above */ };
export const inventoryService = { /* ... */ };
export const customerService = { /* ... */ };
export const employeeService = { /* ... */ };
export const transactionService = { /* ... */ };
export const vendorService = { /* ... */ };
export const analyticsService = { /* ... */ };

// Export all services
export default {
  auth: authService,
  franchise: franchiseService,
  products: productService,
  orders: orderService,
  inventory: inventoryService,
  customers: customerService,
  employees: employeeService,
  transactions: transactionService,
  vendors: vendorService,
  analytics: analyticsService
};